import { PublicClientApplication, AccountInfo, Configuration } from "@azure/msal-browser";

export const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_AZURE_CLIENT_ID!,
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_AZURE_TENANT_ID!}`,
    redirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false,
  },
};

// ✅ One shared instance
export const msalInstance = new PublicClientApplication(msalConfig);

// The browser requests the portal API scope. The API acquires Graph tokens
// on behalf of the signed-in user using the backend app registration.
export const apiScopes = [
  "User.Read",
  "Group.Read.All",
  `api://f3391e09-a7dc-4866-864d-893fb9b351ac/access_as_user`,
];

const REAUTH_REDIRECT_GUARD_KEY = "m365_reauth_redirect_started_at";
const REAUTH_REDIRECT_GUARD_MS = 30000;

function isInteractionRequiredError(error: unknown): boolean {
  const code = (error as any)?.errorCode || (error as any)?.code || "";
  const message = ((error as any)?.message || "").toString().toLowerCase();

  return (
    code === "interaction_required" ||
    code === "login_required" ||
    code === "consent_required" ||
    code === "no_tokens_found" ||
    message.includes("interaction_required") ||
    message.includes("login_required") ||
    message.includes("consent_required")
  );
}

function canStartReauthRedirect(): boolean {
  const now = Date.now();
  const last = Number(sessionStorage.getItem(REAUTH_REDIRECT_GUARD_KEY) || "0");

  if (last && now - last < REAUTH_REDIRECT_GUARD_MS) {
    return false;
  }

  sessionStorage.setItem(REAUTH_REDIRECT_GUARD_KEY, String(now));
  return true;
}

export async function startReauthRedirect(reason?: unknown): Promise<void> {
  if (!canStartReauthRedirect()) {
    return;
  }

  try {
    console.warn("Starting re-auth redirect", reason);
    await msalInstance.loginRedirect({
      scopes: [...apiScopes],
      redirectUri: window.location.origin,
      prompt: "select_account",
    });
  } catch (error: any) {
    const code = error?.errorCode || error?.code || "";
    if (code === "interaction_in_progress") {
      // MSAL is already performing an auth interaction; let it complete.
      return;
    }

    // Allow retry if redirect call failed for a non-interaction reason.
    sessionStorage.removeItem(REAUTH_REDIRECT_GUARD_KEY);
    console.error("Re-auth redirect failed", error);
  }
}

// ✅ Initialize MSAL *once* and set active account
export const initializeMsal = async (): Promise<void> => {
  await msalInstance.initialize();

  const result = await msalInstance.handleRedirectPromise();

  if (result && result.account) {
    msalInstance.setActiveAccount(result.account);
  } else {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
    }
  }
};

/**
 * Acquire an access token for the backend API.
 */
export const acquireTokenForApi = async (): Promise<string> => {
  let account = msalInstance.getActiveAccount();

  if (!account) {
    const accounts: AccountInfo[] = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      account = accounts[0];
      msalInstance.setActiveAccount(account);
    } else {
      throw new Error("No MSAL account found. Please log in again.");
    }
  }

  const request = {
    account,
    scopes: [`api://f3391e09-a7dc-4866-864d-893fb9b351ac/access_as_user`],
  };

  try {
    const response = await msalInstance.acquireTokenSilent(request);
    return response.accessToken;
  } catch (silentError) {
    if (isInteractionRequiredError(silentError)) {
      await startReauthRedirect(silentError);
      return "";
    }

    throw silentError;
  }
};